/* eslint-disable no-unused-vars */
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  assginCreditLine,
  getCreditLineAssignations,
  getProductSubProducts,
} from 'lib/api';
import { getCreditLineFactoringSelectedDecisionEngines } from 'lib/api/decisionEngine';
import ExtendedDatePicker from 'lib/common/ExtendedDatePicker';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import SubPrductAssingnation from './components/Subproducts';

// Type Definitions
interface AssignationModuleProps {
  setOpenShoppingCart: (open: boolean) => void;
  openRequest: any;
  updateStatus: boolean;
  setInAssignation: (inAssignation: boolean) => void;
  setUp: (id: string) => void;
  handleDetails: (id: string) => void;
  onCancel?: () => void;
  isEditingMode?: boolean;
}

// Component Definitions
const AssignationModule = (props: AssignationModuleProps) => {
  const {
    setOpenShoppingCart,
    openRequest,
    updateStatus,
    setInAssignation,
    setUp,
    handleDetails,
    onCancel,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [canUseDecisionEngine, setCanUseDecisionEngine] =
    useState<boolean>(false);
  const [runDecisionEngineClient, setRunDecisionEngineClient] =
    useState<boolean>(false);
  const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(openRequest.amount);
  const [validUntil, setValidUntil] = useState<Date | null>(
    openRequest.validUntil,
  );
  const [days, setDays] = useState<number>(0);
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [subProductConcentrations, setSubProductConcentrations] = useState<
    any[]
  >([]);

  const handleCloseMainDialog = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      setOpenShoppingCart(false);
      handleDetails(openRequest.id);
    }
  }, [openRequest.id, onCancel]);
  const handleCloseConfirmationDialog = useCallback(() => {
    setConfirmationDialog(false);
  }, []);

  const handleChangeAmount = (value: any) => {
    const cleanValue = value.split('.').join('').split('$').join('');
    if (isNaN(cleanValue)) return;
    if (cleanValue < 0) return;
    setAmount(Number(cleanValue));
  };

  const validateSubProductsConcentrationsAmount = () => {
    if (subProductConcentrations.length === 0) {
      alert('Debes seleccionar al menos un subproducto');
      return false;
    }

    // Validate that all subproducts have a subProductId
    const hasAllSubProductIds = subProductConcentrations.every(
      (x: any) => x.id && x.id !== '',
    );
    if (!hasAllSubProductIds) {
      alert(
        'Todos los subproductos deben estar seleccionados antes de continuar',
      );
      return false;
    }

    // Validate that all subproducts have amounts
    const hasAllAmounts = subProductConcentrations.every(
      (x: any) => x.amount && x.amount > 0,
    );
    if (!hasAllAmounts) {
      alert('Todos los subproductos deben tener un monto asignado');
      return false;
    }

    // Validate that individual amounts don't exceed the total
    const hasValidAmounts = subProductConcentrations.every(
      (x: any) => x.amount <= amount,
    );
    if (!hasValidAmounts) {
      alert(
        'El monto de las concentraciones de los subproductos no puede ser mayor al total de la línea.',
      );
      return false;
    }

    return true;
  };

  const validateAmount = () => {
    if (amount <= 0) {
      alert('El monto del total de la línea debe ser mayor a 0');
      return false;
    }
    return true;
  };

  const validateOperationCosts = () => {
    for (const concentration of subProductConcentrations) {
      if (
        !concentration.operationCostFixed ||
        concentration.operationCostFixed <= 0
      ) {
        alert(
          'Debes ingresar un costo de operación fijo para todos los subproductos',
        );
        return false;
      }
      if (
        !concentration.operationCostVariable ||
        concentration.operationCostVariable <= 0
      ) {
        alert(
          'Debes ingresar un costo de operación variable para todos los subproductos',
        );
        return false;
      }
    }
    return true;
  };

  const cleanSubProductsConcentrations = () => {
    const newConcentrations = subProductConcentrations.map((x: any) => {
      const newConcentration = {
        subProductId: x.id,
        operationCostVariable: Number(x.operationCostVariable),
        discountRateVariable: Number(x.discountRateVariable),
        operationCostFixed: Number(x.operationCostFixed),
        discountRateFixed: Number(x.discountRateFixed),
        amount: Number(x.amount),
        ...(x.maxDocumentAmount && {
          maxDocumentAmount: Number(x.maxDocumentAmount),
        }),
      };
      delete x.id;
      return newConcentration;
    });
    return newConcentrations;
  };

  const getConcentrations = async () => {
    setLoading(true);
    const response = await getCreditLineAssignations(dispatch, {
      id: openRequest.id,
    });
    const { subProductAssignations } = response;
    setSubProductConcentrations(subProductAssignations);
    setLoading(false);
  };

  const setUpBusinessSubProducts = async () => {
    setLoading(true);
    const response = await getProductSubProducts(dispatch, openRequest.id);
    setSubProducts(response);
    setLoading(false);
  };

  const needToSetup = async () => {
    await setUpBusinessSubProducts();
    if (updateStatus) {
      return false;
    } else {
      await getConcentrations();
      setValidUntil(openRequest.validUntil);
    }
  };

  const handlePreAssignation = async () => {
    if (!validateAmount()) return;
    if (!validateSubProductsConcentrationsAmount()) return;
    if (!validateOperationCosts()) return;
    setLoading(true);
    const response =
      await getCreditLineFactoringSelectedDecisionEngines(dispatch);
    response.forEach((engine: any) => {
      if (engine.revisionObjectiveId === 'creditLine') {
        setCanUseDecisionEngine(true);
      }
    });
    setLoading(false);
    setConfirmationDialog(true);
  };

  const handleAssignation = async () => {
    setLoading(true);
    const cleannedSubProductsData = cleanSubProductsConcentrations();
    await assginCreditLine(dispatch, {
      id: openRequest.id,
      amount,
      subProductConcentrations: cleannedSubProductsData,
      validUntil,
      typeId: openRequest.productTypeId,
      runDecisionEngineClient,
      runDecisionEnginePayers: false,
    });
    setInAssignation(false);
    showAlert(dispatch, 'success', 'Se ha asignado la línea con éxito.');
    setOpenShoppingCart(false);
    setUp(openRequest.id);
    setLoading(false);
    setConfirmationDialog(false);
  };

  useEffect(() => {
    void needToSetup();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <div style={{ padding: '10px 0px' }}>
        <Typography variant="body1">
          Acá debes ingresar el monto de la línea y si tiene concentraciones que
          la limitan
        </Typography>
      </div>

      <div style={{ padding: '10px 0px' }}>
        <Grid container spacing={2} alignItems={'top'}>
          <Grid item xs={6}>
            <Typography fontWeight={600} variant="body1">
              Monto total
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="description"
              label="Monto"
              type="text"
              value={formatMoney(amount)}
              onChange={(e) => handleChangeAmount(e.target.value)}
            />
          </Grid>
          <ExtendedDatePicker
            validUntil={validUntil}
            setValidUntil={setValidUntil}
            days={days}
            setDays={setDays}
            canDeactivateExpiration
          />
        </Grid>
      </div>
      <div style={{ padding: '10px 0px', gap: '10px' }}>
        <Typography variant="h3">Asignaciones</Typography>
        <SubPrductAssingnation
          concentrations={subProductConcentrations}
          setConcentrations={setSubProductConcentrations}
          amount={amount}
          subProducts={subProducts}
        />
      </div>
      <DialogActions>
        <CustomButton onClick={handleCloseMainDialog} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton onClick={handlePreAssignation} color="primary">
          Enviar
        </CustomButton>
      </DialogActions>
      <Dialog open={confirmationDialog} onClose={handleCloseConfirmationDialog}>
        <DialogTitle>Nueva asignación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Estás a punto de asignar una línea de crédito. Si lo necesitas,
            puedes ejecutar el motor de decisión para revisar la asignación de
            la línea.
          </Typography>
          <FormControl>
            <FormControlLabel
              control={<Checkbox />}
              disabled={!canUseDecisionEngine}
              label="Deseo ejecutar el motor de decisión para el cliente"
              onChange={(e: any) =>
                setRunDecisionEngineClient(e.target.checked)
              }
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseConfirmationDialog}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton onClick={handleAssignation} color="primary">
            Enviar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssignationModule;
